import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";

import { getRedirectResult } from "firebase/auth";
import {
  auth,
  getUserDocument,
  signInAuthUserWithEmailAndPassword,
} from "../../utils/firebase-utils";
import { UserContext } from "../../contexts/user-context";
import { SignUpModalContext } from "../../contexts/signup-modal-context";

import Button from "../form-components/button";
import FormInput from "../form-components/form-input";

import { faRectangleXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import "../form-components/button.styles.css";
import "./sign-in-form.styles.css";
import { SignInModalContext } from "../../contexts/signin-modal-context";
import { ForgottenPasswordContext } from "../../contexts/forgotten-password-context";

const DEFAULT_FORM_FIELDS = {
  email: "",
  password: "",
};

const SignInForm = () => {
  const [formFields, setFormFields] = useState(DEFAULT_FORM_FIELDS);
  const { email, password } = formFields;
  const navigate = useNavigate();
  const { setCurrentUser } = useContext(UserContext);
  const { setOpenSignUpModal } = useContext(SignUpModalContext);
  const { setOpenSignInModal } = useContext(SignInModalContext);

  const { setOpenForgottenPasswordModal } = useContext(
    ForgottenPasswordContext
  );

  useEffect(() => {
    const fetchResult = async () => {
      await getRedirectResult(auth);
    };
    fetchResult();
  }, []);

  const resetFormFields = () => {
    setFormFields(DEFAULT_FORM_FIELDS);
  };

  const handleModelClose = () => {
    setOpenSignInModal(false);
  };

  const handleSignUpClick = () => {
    setOpenSignUpModal(true);
    setOpenSignInModal(false);
  };

  const handleForgottenPasswordClick = () => {
    setOpenForgottenPasswordModal(true);
    setOpenSignInModal(false);
  };

  const setUser = (data) => {
    localStorage.setItem("user", JSON.stringify(data));
    setCurrentUser(data);
  };

  const noAccountMessage = `We don't have an account with that email: sign up to create an account.
  
Please note we're no longer using PrayerLoft - this is a brand new prayer tool just for Hope City and you'll need to create a new account for this site :)
  `;

  const handleSubmit = async (event) => {
    event.preventDefault();

    ReactGA.event({
      category: "form-actions",
      action: "sign-in",
    });

    try {
      const { user } = await signInAuthUserWithEmailAndPassword(
        email,
        password
      );

      const userDocData = await getUserDocument(user);
      const updatedUser = { ...user, role: userDocData.role || "user" };
      await setUser(updatedUser);

      setOpenSignInModal(false);
      resetFormFields();
      navigate("/");
    } catch (error) {
      if (error.code === "auth/wrong-password") {
        alert("Email or password is incorrect");
      } else if (error.code === "auth/user-not-found") {
        resetFormFields();
        alert(noAccountMessage);
      }
      console.log("error signing in", error);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormFields({ ...formFields, [name]: value });
  };

  return (
    <div className="signin-modal">
      <div className="signin-modal-content">
        <div className="signin-modal-header">
          <Button
            className="signin-modal-close-button"
            onClick={handleModelClose}
          >
            <FontAwesomeIcon
              icon={faRectangleXmark}
              style={{ color: "#363832" }}
              size="2xl"
            />
          </Button>
        </div>

        <form onSubmit={handleSubmit}>
          <FormInput
            label="Email"
            inputType="signIn"
            type="email"
            required
            onChange={handleChange}
            name="email"
            value={email}
          />

          <FormInput
            label="Password"
            inputType="signIn"
            type="password"
            required
            onChange={handleChange}
            name="password"
            value={password}
          />

          <div className="signin-button-container">
            <Button type="submit" buttonType="lowlandsGreen">
              Sign In
            </Button>
          </div>

          <section className="signin-links-container">
            <Button
              type="button"
              buttonType="transparent"
              onClick={handleSignUpClick}
            >
              Create an account
            </Button>

            <Button
              type="button"
              buttonType="transparent"
              onClick={handleForgottenPasswordClick}
            >
              Forgot password
            </Button>
          </section>
        </form>
      </div>
    </div>
  );
};

export default SignInForm;
